import { useState } from "react";
import { BookOpen, Rocket, Search, ChevronRight, CheckCircle2, Users, Shield, Building2, Printer } from "lucide-react";
import DashboardShell from "../components/shell/DashboardShell";
import { useAuth } from "../context/AuthContext";

/**
 * In-app user manual. Role-aware:
 *   - Investor (landlord)  → full investor manual
 *   - Admin                → admin ops + superadmin sections
 *   - Tenant               → short tenant guide (reuse at /tenant/help if needed later)
 */
export default function Help() {
  const { user } = useAuth();
  const [query, setQuery] = useState("");
  const role = user?.role;

  const manual = (
    role === "admin" ? ADMIN_MANUAL :
    role === "tenant" ? TENANT_MANUAL :
    INVESTOR_MANUAL
  );

  // Flat search across H3/H4 steps and paragraphs
  const filter = query.trim().toLowerCase();
  const sections = filter
    ? manual.sections.map((s) => ({
        ...s,
        items: s.items.filter((it) =>
          (it.title + " " + (it.body || "")).toLowerCase().includes(filter)
        ),
      })).filter((s) => s.items.length > 0)
    : manual.sections;

  return (
    <DashboardShell>
      <div className="p-6 max-w-4xl mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <div className="overline text-slate-500 flex items-center gap-1"><BookOpen className="w-3 h-3" /> {manual.badge}</div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Outfit" }}>{manual.title}</h1>
            <p className="text-sm text-slate-500 mt-1 max-w-2xl">{manual.subtitle}</p>
          </div>
          <button
            type="button"
            onClick={() => window.print()}
            className="btn-outline-dark text-sm inline-flex items-center gap-2"
            data-testid="help-print"
          >
            <Printer className="w-4 h-4" /> Print / Save PDF
          </button>
        </div>

        <div className="sticky top-0 z-10 bg-[#F8FAFC] pb-3 mb-6">
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search the manual (e.g. 'add property', 'refund', 'revenue')…"
              className="input-clean w-full pl-9"
              data-testid="help-search"
            />
          </div>
        </div>

        {manual.quickstart && (
          <div className="bg-slate-900 text-white border border-slate-800 rounded-sm p-5 mb-8" data-testid="help-quickstart">
            <div className="flex items-center gap-2 mb-3">
              <Rocket className="w-4 h-4" />
              <div className="overline text-white/60">Quick start</div>
            </div>
            <ol className="space-y-2 text-sm">
              {manual.quickstart.map((step, i) => (
                <li key={i} className="flex gap-3">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-white text-slate-900 text-[11px] font-bold inline-flex items-center justify-center">{i + 1}</span>
                  <span className="text-white/90">{step}</span>
                </li>
              ))}
            </ol>
          </div>
        )}

        {sections.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-200 rounded-sm p-10 text-center text-sm text-slate-500" data-testid="help-empty">
            No sections match "{query}". Try a different term.
          </div>
        ) : (
          <div className="space-y-10">
            {sections.map((section) => (
              <section key={section.id} data-testid={`help-section-${section.id}`}>
                <div className="flex items-center gap-2 mb-4">
                  <section.icon className="w-5 h-5 text-slate-500" />
                  <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>{section.title}</h2>
                </div>
                <div className="space-y-4">
                  {section.items.map((item, i) => (
                    <HelpItem key={i} item={item} />
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}

        <div className="mt-12 bg-slate-50 border border-slate-200 rounded-sm p-5 text-sm" data-testid="help-support">
          <div className="font-bold text-slate-900 mb-1">Need more help?</div>
          <div className="text-slate-600">
            Email <a href="mailto:support@realtyvaultmgr.com" className="text-slate-900 font-semibold hover:underline">support@realtyvaultmgr.com</a>
            {" "}or open a feature request from your Settings page. We typically reply within one business day.
          </div>
        </div>
      </div>
    </DashboardShell>
  );
}

function HelpItem({ item }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid={`help-item-${item.title.replace(/\s+/g, "-").toLowerCase()}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left px-5 py-3 flex items-center justify-between gap-3 hover:bg-slate-50 transition"
      >
        <div className="flex items-center gap-2 min-w-0">
          <ChevronRight className={`w-4 h-4 text-slate-400 transition ${open ? "rotate-90" : ""}`} />
          <div className="text-sm font-semibold text-slate-900 truncate">{item.title}</div>
          {item.tag && (
            <span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm ${item.tag === "Superadmin" ? "bg-violet-50 text-violet-700 border border-violet-200" : "bg-slate-100 text-slate-600 border border-slate-200"}`}>
              {item.tag}
            </span>
          )}
        </div>
      </button>
      {open && (
        <div className="px-5 pb-4 pt-1 border-t border-slate-100">
          {item.body && <p className="text-sm text-slate-700 leading-relaxed mb-3">{item.body}</p>}
          {item.steps && (
            <ol className="space-y-1.5 text-sm text-slate-700">
              {item.steps.map((s, i) => (
                <li key={i} className="flex gap-2">
                  <span className="text-slate-400 font-mono text-xs mt-0.5 min-w-[1.25rem]">{i + 1}.</span>
                  <span>{s}</span>
                </li>
              ))}
            </ol>
          )}
          {item.tips && (
            <div className="mt-3 border-l-2 border-emerald-500 bg-emerald-50 text-emerald-900 px-3 py-2 text-xs rounded-r-sm">
              <div className="flex items-center gap-1.5 font-semibold mb-0.5"><CheckCircle2 className="w-3.5 h-3.5" /> Tip</div>
              {item.tips}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ============================================================ CONTENT */

const INVESTOR_MANUAL = {
  title: "Investor user manual",
  subtitle: "Everything you need to run your portfolio end-to-end — from adding your first property to shipping a Lender Packet at refi time.",
  badge: "Investor guide",
  quickstart: [
    "Add your first property via Properties → Add Property. Address auto-geocodes + pulls a Street View photo.",
    "Set annual taxes + insurance on each property (the green/amber shield icon on the Properties row) so the SREO coverage check works. Also set the property's next Taxes Due Date — it powers a red 30-day dashboard alert + the Reports → Taxes Due PDF.",
    "Create a tenant account under Tenants → Invite Tenant so they can pay rent online — or click 'Send login' on any tenant row to email them a fresh portal URL + temp password in one click.",
    "Wire up Stripe Connect in Settings to accept online card + ACH rent payments.",
    "Generate a state-compliant residential lease in seconds — Lease Vault → Generate AI Lease (powered by Claude with automatic statutory citations).",
    "Record your loans + borrowers — Borrowers Library lets you track every borrower across multiple loans; Loans dashboard surfaces a red badge on the sidebar when any loan matures within 30 days.",
    "Schedule a quarterly investor report from Reports → Email Bundle, or hit Email Lender Packet from the SREO report when a lender asks.",
  ],
  sections: [
    {
      id: "portfolio",
      title: "Managing your portfolio",
      icon: Building2,
      items: [
        {
          title: "Add a new property",
          body: "Properties are the foundation — everything else (leases, loans, expenses, valuation) links back to a property.",
          steps: [
            "Go to Properties → Add Property.",
            "Enter address + property type + units + purchase price.",
            "We'll auto-geocode the address and fetch a Google Street View photo facing the entrance.",
            "If the angle is wrong, click the property → Retake photo → nudge the camera heading.",
            "Use Properties → Retake photos (batch mode) before quarterly reports to refresh every property's framing.",
          ],
          tips: "Entering an accurate market_value lets the Valuation module compute cap-rate, NOI yield, and benchmark you against asset-type norms.",
        },
        {
          title: "Invite tenants + manage leases",
          steps: [
            "Tenants → Add Tenant → choose 'Invite tenant' (sends them a password-set email) or enter a password directly.",
            "Leases → Add Lease → pick the property + the tenant, set dates and monthly rent.",
            "Once the lease is active, the tenant can sign in at /tenant-login and see their rent statement.",
          ],
        },
        {
          title: "Accept online rent payments (Stripe)",
          steps: [
            "Settings → Payment gateways → Connect Stripe.",
            "Complete the Stripe Connect onboarding (identity + bank account).",
            "Once Connected, your tenants will see a 'Pay rent' button in the tenant portal.",
            "Payouts go directly to your bank — we never custody funds.",
          ],
          tips: "The 2.9% + $0.30 card fee can optionally be passed to tenants; toggle 'Who pays the processing fee?' in Payment gateway settings.",
        },
        {
          title: "Record off-platform rent payments (check / cash / ACH / Zelle)",
          steps: [
            "Sidebar → Rental Payments → click the green 'Record payment' button.",
            "Pick the lease/tenant — the monthly rent autofills, formatted as US currency (e.g. $5,000.00).",
            "Choose the payment method (check, cash, ACH, Zelle, CashApp, money order, other).",
            "Add a check number, paid-on date, and optional notes — save.",
            "The row appears immediately in the dashboard table; click the trash icon to delete a mis-keyed entry.",
          ],
          tips: "Stripe-originated rent payments are NOT shown here — they live in the Tenant ledger. This dashboard is purely for payments you collected outside the platform so your portfolio totals stay accurate.",
        },
        {
          title: "Run a date-range Payments Report (PDF + CSV)",
          body: "Need to send your accountant a clean monthly / quarterly / annual rent-roll of off-platform payments? Use the Payments Report panel on the Rental Payments dashboard.",
          steps: [
            "Sidebar → Rental Payments → scroll to the 'Payments Report' panel just below the stat cards.",
            "Pick a Start date and End date (or use the 'This month / Last 30d / YTD' presets).",
            "Pick a Sort order: Date newest first / oldest first / Property (A-Z) / Amount (largest first).",
            "Click 'Generate report' — you'll see 4 KPI cards (count, total collected, average, methods used), a by-method breakdown, and the full detail table inline.",
            "Click 'Download CSV' for Excel / QuickBooks, or 'Download PDF' for a printable, landscape report with header, KPIs, by-method breakdown and a detail table with totals.",
          ],
          tips: "The sort order is honored in BOTH the PDF and the CSV, so the file your accountant receives matches exactly what you previewed on screen.",
        },
        {
          title: "Track expenses + vendors",
          steps: [
            "Vendors → Add Vendor → set category (plumbing/electrical/HVAC/etc.) and optional annual budget.",
            "Expenses → Add Expense → link to property + vendor + category.",
            "Reports → Vendor Spend Analytics shows top-3 vendors per category and variance against budget.",
          ],
        },
        {
          title: "Set annual taxes & insurance per property",
          body: "Required for the SREO Tax + Insurance Coverage check. Without them, a property is excluded from the underwriting math.",
          steps: [
            "Go to Properties → click the shield icon in any row's actions column (amber = not configured, green = both fields set).",
            "Enter Annual taxes ($) and Annual insurance ($). Total / year is shown live.",
            "Save — the SREO report's coverage banner immediately recalculates.",
          ],
          tips: "Hover the shield icon to see current values without opening the dialog. Properties without T&I configured are flagged 'no_ti' and don't appear in the coverage analysis.",
        },
        {
          title: "Approve vendor invoices in bulk",
          steps: [
            "Vendors → Inbox tab shows pending invoices submitted via the public vendor portal.",
            "Click 'Pay All Approved' to mass-pay every invoice with status=approved in one go.",
            "Vendors are auto-emailed when their invoice is approved, rejected, or paid.",
          ],
          tips: "The vendor portal magic link lets vendors submit invoices with PDF/photo attachments without needing a login. Send them their portal URL from Vendors → click vendor → Send Portal Link.",
        },
      ],
    },
    {
      id: "loans",
      title: "Loans & financing",
      icon: BookOpen,
      items: [
        {
          title: "Record a mortgage / commercial loan",
          steps: [
            "Loans → Add Loan.",
            "Fill in principal, rate, term, origination date, and loan type (amortizing or interest-only).",
            "System auto-generates a full amortization schedule + monthly payment + total interest.",
            "Loan maturity alerts appear on the Overview dashboard at 30 / 60 / 90 days.",
          ],
        },
        {
          title: "Log a loan payment",
          body: "Each recorded payment decreases your outstanding principal so refi-impact math stays accurate.",
          steps: [
            "Loans → click a loan → Payments tab → Add Payment.",
            "Enter date + amount. We'll split it into principal/interest per the schedule.",
            "Set a target payoff date and the plan card will show on-track/ahead/behind variance.",
          ],
        },
        {
          title: "See refi savings at today's market rate",
          steps: [
            "On Overview, click any row in the 'Interest rates today' widget (Treasury / SOFR / 30-Year Mortgage).",
            "Dialog reprices every loan at that rate using remaining balance + remaining term.",
            "Rows sorted savings-first with monthly + annual delta.",
          ],
        },
        {
          title: "Run a rate-shock stress test on a loan",
          body: "What lenders ask for during refi conversations: how does the loan perform if rates jump 1, 2, or 3 percentage points?",
          steps: [
            "Loans → click the trending-up icon in any loan's row.",
            "Dialog shows current vs +100 / +200 / +300 bps shocks side-by-side.",
            "Each shock displays new monthly P&I, annual debt service, DSCR, and break-even occupancy — color-tinted (green/amber/red) by lender thresholds.",
            "Sub-deltas under each cell show '+$634/mo' and '−0.13x DSCR' so the impact is visceral.",
          ],
          tips: "DSCR ≥ 1.25x is the typical lender minimum for stabilized CRE. Break-even occupancy > 90% means the asset can't carry its debt if a major tenant moves out — both thresholds are flagged automatically.",
        },
        {
          title: "Override a loan's contractual maturity date",
          body: "By default the maturity is computed from Loan Date + Term, but extensions, refis, and assumed loans often have non-standard maturities. Set the override and every alert + sidebar badge respects it.",
          steps: [
            "Loans → Edit any loan.",
            "Set the optional 'Maturity date' field below Loan number / Loan date.",
            "Leave blank to revert to the auto-computed value.",
          ],
          tips: "Override applies to the Loans dashboard tint, sidebar Loan Maturity badge, Reports → Loan Portfolio, the weekly maturity email, and the SREO export — one place, everywhere.",
        },
        {
          title: "Mortgage Calculator with 'Good / Poor Purchase' verdict",
          body: "Add an optional NOI and Purchase Price to the calculator and we'll show the cap rate, leverage spread vs the loan rate, DSCR, and a single-glance verdict pill.",
          steps: [
            "Loans → Calculator (top-right) → fill amount, rate, term as usual.",
            "Scroll to 'Purchase underwriting' → enter Annual NOI + Purchase price.",
            "Green 'Good Purchase' pill appears when cap rate > interest rate AND DSCR ≥ 1.25x.",
            "Red 'Poor Purchase' pill flags negative leverage or sub-1.25x DSCR.",
          ],
          tips: "Hover the verdict pill for the exact spread + DSCR. Use 'Add loan with these values' to convert the model into a real loan record once you sign the LOI.",
        },
        {
          title: "Loan row tinting by maturity (green / yellow / red)",
          body: "The Loans dashboard now tints each row by days-until-maturity so you can triage at a glance without opening individual alerts.",
          steps: [
            "Open Loans → look at row backgrounds in the loans table.",
            "≤30 days = red, ≤60 days = yellow, ≤90 days = green, beyond 90 days = no tint.",
            "Past-due loans stay untinted and surface in the Critical bucket above the table.",
          ],
        },
        {
          title: "Quick portfolio performance from the Loans tab",
          body: "Click 'View Portfolio Performance' (top-right of the Loans page) to jump to the cap-rate dashboard without breaking your loan-review flow.",
        },
      ],
    },
    {
      id: "reporting",
      title: "Reporting & investor updates",
      icon: BookOpen,
      items: [
        {
          title: "Generate a quarterly report bundle",
          steps: [
            "Reports → Bundle → choose date range (e.g. Q1 2026).",
            "Click 'Download ZIP' to get a zip of Rent Roll + Loan Portfolio + Portfolio Snapshot + Expense Report PDFs (all landscape Letter format).",
            "Or click 'Email bundle' to deliver it to your stakeholder list in one click.",
          ],
          tips: "Set up a stakeholder email list in Settings → Report Recipients so every bundle goes to the same CPA + co-investors automatically.",
        },
        {
          title: "Run a Property Taxes Due report (date range PDF)",
          body: "Avoid county penalties — every property with a Taxes Due Date inside the window shows up in a single printable schedule.",
          steps: [
            "Reports → Taxes Due tab.",
            "Pick a start + end date (default = current calendar year). Quick presets cover 'This year' and 'Next 90 days'.",
            "Generate to preview: KPI strip (count / overdue / due-soon / total annual taxes) + a color-coded table (red rows = overdue, amber = within 30 days).",
            "Click 'Download PDF' for a Letter-size schedule with the same color-coding — safe to forward to your CPA or bookkeeper.",
          ],
          tips: "Properties dashboard also surfaces a red 30-day banner whenever any property's Taxes Due Date is within the window — so you catch this without needing to open Reports.",
        },
        {
          title: "Read the Schedule of Real Estate Owned (SREO)",
          body: "The investor's master scoreboard — every property's cost basis, market value, debt, NOI, DSCR, and now annual taxes & insurance with portfolio totals.",
          steps: [
            "Reports → SREO tab.",
            "13-column table: Property / Address / Type / Units / Purchase date / Purchase price / Current value / Taxes (yr) / Insurance (yr) / NOI / Cap rate / Loan balance / Equity.",
            "Dark portfolio totals row at the bottom aggregates all 13 columns.",
            "Tax + Insurance Coverage banner above the table flags properties where cash flow after debt service can't cover next year's T&I obligation.",
          ],
        },
        {
          title: "Email a Lender Packet (SREO + Rent Roll)",
          body: "One-click underwriting story for lenders. Recipient gets a polished email with two matching landscape PDFs in their inbox.",
          steps: [
            "Reports → SREO → 'Email Lender Packet' (top-right).",
            "Add recipient emails (auto-saves to your saved lender list for next time) + an optional message.",
            "Tick 'Attach current-month Rent Roll' to bundle live collections data alongside the SREO.",
            "Click Send. Subject + body adapt automatically based on whether it's a packet or solo SREO.",
          ],
          tips: "Both PDFs are landscape Letter (792×612pt) with consistent branding so lenders can review them side-by-side cleanly.",
        },
        {
          title: "Read the Tax + Insurance Coverage check",
          body: "CPA-style portfolio review: which properties can't fund their next tax bill + insurance premium from cash flow after debt service?",
          steps: [
            "Reports → SREO. Coverage banner sits above the detail table.",
            "Properties classified as 'covered' (≥1.5x), 'tight' (1.0x–1.5x), 'underfunded' (<1.0x), or 'no_ti' (no T&I configured).",
            "Expand the banner to see the per-property breakdown sorted underfunded-first.",
            "Click the shield icon on a Properties row to set / fix annual taxes + insurance on the fly.",
          ],
        },
        {
          title: "Track portfolio performance + cap-rate",
          body: "Performance page shows NOI yield + cap-rate per property with benchmark badges (below/at/above typical for your asset type). Hospitality properties (hotel/motel) automatically use ADR × occupancy × 365 instead of leases for revenue.",
        },
        {
          title: "Run a property valuation",
          steps: [
            "Valuation → choose a property → Run.",
            "We blend RentCast comps + NOI / cap-rate income approach + cost approach (RCN − depreciation + land) into a single valuation.",
            "Use the slider to adjust blend weight between comp-based and income-based.",
            "Residential properties default to 5% income / 75% sales / 20% cost. Commercial defaults to 60% income / 40% sales.",
          ],
        },
      ],
    },
    {
      id: "whats-new",
      title: "What's new — Feb 2026 features",
      icon: BookOpen,
      items: [
        {
          title: "Rental Payments — date-range report with PDF + CSV download",
          body: "The Rental Payments dashboard now has a full Payments Report panel for sending clean monthly / quarterly / annual rent payment summaries to your accountant or property partners.",
          steps: [
            "Sidebar → Rental Payments → 'Payments Report' panel (right under the stat cards).",
            "Pick Start date / End date — or use 'This month / Last 30d / YTD' presets.",
            "Pick a Sort: Date newest / Date oldest / Property (A-Z) / Amount (largest first).",
            "Generate → view 4 KPI cards (count, total, average, methods used), a by-method breakdown, and the detail table inline.",
            "Download CSV (Excel / QuickBooks-ready) or Download PDF (printable, landscape with totals).",
          ],
          tips: "The sort order is honored across the preview, PDF, and CSV — what you see is what your accountant receives. Off-platform only — Stripe rent payments are tracked separately in the Tenant ledger.",
        },
        {
          title: "Amount Paid field → US currency on blur (Record a tenant payment)",
          body: "Money entry on the 'Record a tenant payment' form now normalizes to two decimals automatically. Type `2500` → blur → `2,500.00`. Type `1234.5` → `1,234.50`. Lease auto-fill pads to cents too.",
          tips: "Same comma-grouping while typing as before — the 2-decimal lock-in just fires on blur and on lease select.",
        },
        {
          title: "Portfolio Reporting — color-coded tabs & Taxes Due KPI tints",
          body: "Every tab on the Portfolio Reporting page now has its own brand color (blue / brown / green / black / red / orange) and the Taxes Due tab gained a clickable 'Late Fees Accruing' KPI filter.",
          steps: [
            "Reports → tabs are now tinted: Rent Roll = blue · Property Expenses = brown · Loan Portfolio = green · Taxes Due = black · SREO = red · Combined SREO + Rent Roll = orange.",
            "Taxes Due tab → 5 KPI boxes (Properties / Overdue / Due 30d / Late Fees Accruing / Total Annual).",
            "Click the 'Late Fees Accruing' KPI to filter the table + PDF to only accruing properties; click again or use the Clear-filter banner to restore.",
            "Filtered PDF carries an 'ACCRUING ONLY' red pill in the title so printed reports never get confused with the full report.",
          ],
        },
        {
          title: "AI Residential Lease Generator (with state-compliance scans)",
          body: "Powered by Claude 4.5 Sonnet. Generates a fully-formatted residential lease with the correct statutory citations for the property's state. Monthly background scans re-check saved leases against new state laws and surgically redraft only affected clauses.",
          steps: [
            "Lease Vault → Generate AI Lease.",
            "Pick lease type (commercial / residential), confirm state.",
            "AI runs in the background (≤2 min). You'll get a notification when it's ready.",
            "Output is editable Word (.docx) + PDF. No watermarks, no DRAFT stamps.",
          ],
          tips: "Per-clause 'Regenerate this clause' surgically rewrites only the impacted section when a new statute lands.",
        },
        {
          title: "Borrowers Library + Loan-maturity sidebar badge",
          body: "Track every borrower as a first-class entity. Sidebar Loans item shows a red badge when any loan matures within 30 days (amber ≤60, green ≤90).",
          steps: [
            "Loans → Borrowers tab → Add Borrower.",
            "When adding a Loan, pick a borrower from the dropdown.",
            "Sidebar badge keeps you on top of refi windows on every page.",
          ],
        },
        {
          title: "Vendor & Tenant Portal — one-click 'Send login' email",
          body: "Each Vendors and Tenants row has a 'Send login' pill that emails a magic-link portal URL (vendors) or fresh temp password + URL (tenants) in one click.",
          steps: [
            "Vendors / Tenants → click 'Send login' on any row with an email on file.",
            "Confirm prompt → branded HTML email fires immediately.",
            "Disabled when there's no email on file.",
          ],
        },
        {
          title: "Property Taxes Due report + 30-day dashboard alert",
          body: "Add a Taxes Due Date to any property and we'll surface a red 30-day alert on the Properties dashboard AND power a downloadable date-range PDF schedule from Reports → Taxes Due.",
          steps: [
            "Properties → edit any property → set Taxes Due Date (the next county/municipal payment).",
            "Properties dashboard: if any property is overdue or due within 30 days, a red 'Property taxes due soon' banner appears at the top — each row shows days remaining + annual tax amount.",
            "Reports → Taxes Due tab → pick a window (default = this year) → preview + Download PDF.",
            "PDF is color-coded: red rows = overdue, amber = within 30 days, white = upcoming.",
          ],
          tips: "Quick presets cover 'This year' (Jan 1 → Dec 31) and 'Next 90 days' so you can flip between annual planning and immediate cash-flow alerts in one click.",
        },
        {
          title: "Loans Only plan — $69/mo lender / broker tier",
          body: "A focused subscription that exposes only the loan-stack tools (no properties, tenants, or leases). Built for brokers, lenders, and capital-stack-focused operators who don't need property management.",
          steps: [
            "Pricing → Loans Only (cheapest specialty tier on the lineup).",
            "Includes: unlimited loans, full amortization + GAAP-style payment accounting, Mortgage Calculator with Good/Poor Purchase verdict, Refi modeler + rate-shock stress test, Borrowers Library, maturity alerts.",
            "Sidebar is stripped to Loans, Reports, Billing, Settings, Help — zero property clutter.",
            "PFS Generator available as a +$20/mo add-on (see next section).",
          ],
          tips: "Upgrade to Pro any time and your data carries forward — Loans Only is a subset, not a different account.",
        },
        {
          title: "Personal Financial Statement add-on (+$20/mo)",
          body: "Bolt the PFS Generator onto Starter or Loans Only for $20/mo extra. Pro & Enterprise plans include PFS natively — no add-on needed.",
          steps: [
            "Billing → click 'Add Personal Financial Statement add-on' under the Starter or Loans Only card → subscribe.",
            "PFS sidebar item unlocks immediately on next page load.",
            "Add-on users get the full PFS generator: real-estate schedule auto-sync, Lender PDF, snapshot history, share-link with audit, Email to Lender.",
            "If billing lapses, you get a 30-day read-only grace period before PFS hides — saved data is never deleted.",
          ],
          tips: "Add-on users see the green 'Email to lender' button but NOT 'Lender Package' (that bundle is Pro+ only — it pulls rent roll & lease binder data Starter/Loans Only don't have).",
        },
        {
          title: "Property Taxes Due report (date-range PDF) + dashboard alert",
          body: "Add a Taxes Due Date to any property and we'll surface a red 30-day alert on the Properties dashboard AND power a downloadable date-range PDF from Reports → Taxes Due.",
          steps: [
            "Properties → edit any property → set Taxes Due Date.",
            "Properties dashboard: red banner appears when any property is overdue or due within 30 days.",
            "Reports → Taxes Due tab → pick a window → Generate → Download PDF.",
            "PDF rows color-coded: red = overdue, amber = within 30 days, white = upcoming.",
          ],
          tips: "Quick presets cover 'This year' (Jan 1 → Dec 31) and 'Next 90 days'. CPAs love the printable schedule.",
        },
        {
          title: "Loans — maturity override + Calculator verdict + row tinting",
          body: "Three connected upgrades to the Loans module landed together.",
          steps: [
            "Edit Loan now exposes an optional Maturity Date override (extensions, assumed loans, custom payoff schedules).",
            "Calculator gains a Purchase Underwriting block: enter NOI + Purchase Price → cap rate + Good/Poor Purchase verdict pill.",
            "Loans table rows are tinted red/yellow/green by days-to-maturity (≤30/≤60/≤90).",
          ],
          tips: "The verdict pill uses positive-leverage (cap > rate) AND DSCR ≥ 1.25x — the two thresholds most lenders underwrite to.",
        },
        {
          title: "Dashboard color-tints for at-a-glance scanning",
          body: "Overview, Properties, Lease Vault, Payments, CRE Comps, Vendors, and the PFS now use consistent color-tinted KPI cards so you can spot what matters before reading a single number.",
          tips: "Green = healthy / value; yellow = attention; red = risk / liability; purple = lender-facing; brown/amber = informational. The Send-Portfolio-to-Lender card uses purple to match the lender ecosystem tinting.",
        },
        {
          title: "Product tour no longer auto-launches",
          body: "The 2-minute onboarding tour is now opt-in. Restart it any time from Settings → 'Restart product tour' — useful when teaching a new teammate or admin.",
        },
        {
          title: "Sidebar — bold collapsible sections",
          body: "Sidebar is grouped into collapsible bold-black headers: WORKSPACE, PORTFOLIO, OPERATIONS, FINANCE, INSIGHTS, ACCOUNT. Preference persists per-browser.",
          tips: "Click a section header to collapse/expand. Useful for focusing on one area at a time without 22 menu items distracting you.",
        },
      ],
    },
    {
      id: "settings",
      title: "Account & billing",
      icon: BookOpen,
      items: [
        {
          title: "Change password / update profile",
          steps: ["Settings → Profile → Update.", "Or click the Forgot password link on the login page."],
        },
        {
          title: "Upgrade / cancel your subscription",
          steps: [
            "Billing → choose a plan → Stripe Checkout.",
            "Plans (monthly): Starter $29 · Pro $99 · Enterprise $199 · Fund Manager $299 · Enterprise Multi $499.",
            "Cancel from Billing → Cancel plan anytime — changes take effect at next renewal.",
          ],
        },
      ],
    },
  ],
};

const ADMIN_MANUAL = {
  title: "Admin & Superadmin manual",
  subtitle: "Operate the platform: onboard customers, monitor revenue, triage demo requests, and manage integrations.",
  badge: "Platform ops",
  quickstart: [
    "Hit Overview — use 'Create new account' to onboard a customer in 15 seconds.",
    "Check Platform Revenue daily — MRR, churn, attribution by source, top-earning investors.",
    "Triage Demo Requests promptly — leads go stale fast.",
    "Monitor Integrations for gateway health (Stripe, Resend, Plaid, Google Maps).",
  ],
  sections: [
    {
      id: "ops",
      title: "Daily operations",
      icon: Users,
      items: [
        {
          title: "Create a new account (investor / tenant / admin)",
          tag: "Admin",
          steps: [
            "Overview → 'Create new account' card (top of the page).",
            "Pick role — Investor (paying customer), Tenant (assigned to an investor), or Admin (elevated platform access).",
            "Fill name + email + optional company/phone. For tenant role, also choose the investor to assign.",
            "Submit. A secure temp password is generated (e.g. Solid-Harbor-5527) and a welcome email is sent via Resend.",
            "The success panel shows the temp password with a Copy button — share it manually if the welcome email is blocked.",
          ],
          tips: "Created accounts appear in the 'Created by you' ledger for 72h, with a 'Copy password' re-reveal button. After 72h the ledger entry auto-purges via Mongo TTL for security.",
        },
        {
          title: "Triage a demo request",
          tag: "Admin",
          steps: [
            "Sidebar → Demo Requests.",
            "New leads have status 'new' and an unread badge.",
            "Click a row → change status to 'contacted' → 'qualified' → 'booked' or 'disqualified'.",
            "Add internal notes from the same panel (private; never visible to the prospect).",
          ],
        },
        {
          title: "Impersonate an investor",
          body: "Useful for support tickets where you need to see exactly what the investor sees.",
          tag: "Superadmin",
          steps: [
            "Sidebar → Investors → pick a row.",
            "Click 'Impersonate' — you'll be signed in as that investor in a new tab.",
            "All actions are audit-logged with impersonated_by = <your admin id>.",
          ],
        },
      ],
    },
    {
      id: "revenue",
      title: "Revenue & growth",
      icon: BookOpen,
      items: [
        {
          title: "Read the Platform Revenue dashboard",
          tag: "Admin",
          body: "Sidebar → Platform Revenue. Top cards: MRR, active subscribers, churn (last 30d), signups today.",
          steps: [
            "Tier breakdown chart = how your MRR splits across Starter / Pro / Enterprise.",
            "Acquisition attribution = signups bucketed by source (direct, referral, demo, etc.) with conversion to active subscription.",
            "Top-earning investors table = subscription revenue + rent volume per customer, with ZIP/Email bundle shortcut.",
          ],
        },
        {
          title: "Change an investor's subscription plan",
          tag: "Superadmin",
          body: "For comps, refunds, or manual upgrades.",
          steps: [
            "Sidebar → Investors → open the row → 'Plan' dropdown → select new plan.",
            "Expiry auto-resets to +30 days unless you enter a custom date.",
          ],
        },
      ],
    },
    {
      id: "integrations",
      title: "Integrations & health",
      icon: Shield,
      items: [
        {
          title: "Connect / rotate Resend (transactional email)",
          tag: "Superadmin",
          body: "Transactional emails (welcome, password-reset, report-bundle-email) all go through Resend.",
          steps: [
            "Admin → Integrations → Resend → paste API key → Save.",
            "DNS verification must be completed on your domain (support.realtyvaultmgr.com). Use Cloudflare or your registrar.",
            "Until DNS is verified, Resend won't deliver to arbitrary addresses. In that state the UI shows an amber banner and the temp password fallback is used.",
          ],
        },
        {
          title: "Monitor gateway health",
          tag: "Admin",
          steps: [
            "Admin → Integrations.",
            "Status chips show Connected / Disconnected / Error for each of: Stripe Connect, Plaid, Resend, Google Maps, RentCast, Emergent LLM.",
          ],
        },
      ],
    },
    {
      id: "security",
      title: "Security",
      icon: Shield,
      items: [
        {
          title: "Password reset for a user who lost access",
          tag: "Admin",
          steps: [
            "Ask the user to click 'Forgot password' on the login screen.",
            "If Resend is in MOCK mode, use the 'Created by you' ledger on Overview to find their temp password (valid 72h).",
            "Otherwise re-create the account via 'Create new account' (old one must be deleted first via the Investors panel).",
          ],
        },
        {
          title: "Audit impersonation + admin actions",
          tag: "Superadmin",
          body: "Every admin-side mutation (impersonate, plan changes, account creation, deletions) is recorded with admin_id + timestamp. Ask engineering for a dump from `admin_audit_log` collection if a compliance review is needed.",
        },
      ],
    },
  ],
};

const TENANT_MANUAL = {
  title: "Tenant portal guide",
  subtitle: "Pay rent, file maintenance tickets, and access your lease — all in one place.",
  badge: "Tenant guide",
  quickstart: [
    "Sign in at /tenant-login using the credentials your property manager gave you.",
    "Click 'Pay rent' on My Portal to pay via card or linked bank account.",
    "Use 'Report an issue' to file a maintenance ticket with photos.",
  ],
  sections: [
    {
      id: "pay",
      title: "Paying rent",
      icon: BookOpen,
      items: [
        {
          title: "Pay online with a card",
          steps: [
            "My Portal → Pay rent → enter amount (defaults to your monthly rent).",
            "Enter card details on the Stripe-hosted checkout page.",
            "Confirmation + receipt are emailed automatically.",
          ],
        },
        {
          title: "Pay via bank transfer",
          body: "Your landlord may offer ACH / bank transfer instructions. If they have, the deposit details (bank name, routing, last-4 of account, reference line) are shown on My Portal.",
        },
      ],
    },
    {
      id: "maintenance",
      title: "Maintenance",
      icon: BookOpen,
      items: [
        {
          title: "Report a repair",
          steps: [
            "My Portal → Report an issue.",
            "Describe the problem, choose urgency (Routine / Urgent / Emergency).",
            "Attach photos if helpful.",
            "Your landlord gets notified immediately and you'll see status updates in the portal.",
          ],
        },
      ],
    },
  ],
};
